import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { to: "/shiatsu", label: "Shiatsu", id: "shiatsu" },
  { to: "/begleitung", label: "1:1 Begleitung", id: "begleitung" },
  { to: "/yoga", label: "Yoga", id: "yoga" },
  { to: "/aktuelles", label: "Aktuelles", id: "aktuelles" },
  { to: "/ueber-mich", label: "Über mich", id: "ueber-mich" },
  { to: "/kundenstimmen", label: "Kundenstimmen", id: "kundenstimmen" },
  { to: "/kontakt", label: "Kontakt", id: "kontakt" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled || open
            ? "bg-creme/85 backdrop-blur-md border-b border-anthrazit/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12 md:py-5">
          <Link
            to="/"
            data-testid="nav-brand-link"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3"
          >
            <img
              src="/images/logo-transparent.png"
              alt="Logo – Herz über einem gewundenen Weg"
              className="h-12 w-auto"
            />
            <img
              src="/images/signatur-carmen.png"
              alt="Carmen Hackradt"
              className="h-10 w-auto md:h-11"
            />
          </Link>

          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={open}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-anthrazit/15 text-anthrazit transition-colors duration-300 hover:border-rot hover:text-rot"
          >
            {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-creme px-8"
          >
            <nav className="flex flex-col gap-2" aria-label="Hauptnavigation">
              {[{ to: "/", label: "Startseite", id: "home" }, ...LINKS].map((l, i) => (
                <span key={l.id} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.7, delay: 0.06 * i, ease: EASE }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-link-${l.id}`}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `block py-2 font-serif text-3xl transition-colors duration-300 md:text-4xl ${
                          isActive ? "italic text-rot" : "text-anthrazit"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.span>
                </span>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
